import { type RefObject, useEffect } from 'react';

import {
  SHELL_TAB_BAR_EXPAND_EVENT,
  SHELL_TAB_BAR_SETTLE_EVENT,
} from '@/core/hooks/use-shell-tab-bar-compact';
import {
  triggerSelectionChangedHaptic,
  triggerSelectionEndHaptic,
  triggerSelectionStartHaptic,
} from '@/core/lib/dnd-haptics';

const SPRING = 0.28;
const SETTLE_EPSILON = 0.35;
const SCRUB_HOST_SCALE = 1.04;
/** Finger must move this far before we treat the gesture as a scrub (scale bump). */
const SCRUB_MOVE_THRESHOLD_PX = 14;
/** Fixed glass capsule size — same for every non-QR tab. */
const GLOW_W_PX = 64;
const GLOW_H_PX = 52;
/** Match compact hook — slow scrub-release scale settle. */
const SCALE_SETTLE_MS = 700;

type GlowState = {
  x: number;
  w: number;
  h: number;
  opacity: number;
  scale: number;
};

type ShellTabBarTouchGlowOptions = {
  /** Called when the finger lifts on a tab (navigate / open QR). */
  onActivate: (button: HTMLElement) => void;
};

function isQrTab(button: HTMLElement): boolean {
  return button.classList.contains('shell-mobile-tab-bar__qr-tab');
}

function readScaleLayerScale(host: HTMLElement): number {
  const scaleLayer = host.querySelector(
    '.shell-mobile-tab-bar-scale',
  ) as HTMLElement | null;
  const el = scaleLayer ?? host;
  const layoutW = el.offsetWidth;
  if (layoutW > 1) {
    const visual = el.getBoundingClientRect().width / layoutW;
    if (visual > 0.01) {
      return visual;
    }
  }
  const styles = getComputedStyle(host);
  const compact =
    Number.parseFloat(
      styles.getPropertyValue('--shell-tab-bar-compact-scale'),
    ) || 1;
  const scrub =
    Number.parseFloat(styles.getPropertyValue('--shell-tab-bar-scrub-scale')) ||
    1;
  const product = compact * scrub;
  return product > 0.01 ? product : 1;
}

function glowAnchorForButton(button: HTMLElement): HTMLElement {
  const iconSlot = button.querySelector('.shell-mobile-tab-bar__icon-slot');
  if (iconSlot instanceof HTMLElement) {
    return iconSlot;
  }
  const avatar = button.querySelector('.shell-mobile-tab-bar__avatar');
  if (avatar instanceof HTMLElement) {
    return avatar;
  }
  return button;
}

/**
 * iOS-26-like finger scrub on the floating tab bar.
 * Soft spring-followed glass highlight + activate the tab under the finger on release.
 */
export function useShellTabBarTouchGlow(
  enabled: boolean,
  hostRef: RefObject<HTMLElement | null>,
  { onActivate }: ShellTabBarTouchGlowOptions,
): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const host = hostRef.current;
    if (!host) {
      return;
    }

    const scaleLayer =
      (host.querySelector(
        '.shell-mobile-tab-bar-scale',
      ) as HTMLElement | null) ?? host;

    let glow = scaleLayer.querySelector(
      '.shell-mobile-tab-bar__touch-glow',
    ) as HTMLDivElement | null;
    if (!glow) {
      glow = document.createElement('div');
      glow.className = 'shell-mobile-tab-bar__touch-glow';
      glow.setAttribute('aria-hidden', 'true');
      scaleLayer.insertBefore(glow, scaleLayer.firstChild);
    }

    let activeButton: HTMLElement | null = null;
    let scrubbing = false;
    let scrubScaleArmed = false;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let pointerRaf: number | null = null;
    let springRaf: number | null = null;
    let pendingX = 0;
    let pendingY = 0;
    let lastPointerX = 0;
    let lastPointerY = 0;
    let startX = 0;
    let startY = 0;

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    const current: GlowState = {
      x: 0,
      w: GLOW_W_PX,
      h: GLOW_H_PX,
      opacity: 0,
      scale: 0.88,
    };
    const target: GlowState = {
      x: 0,
      w: GLOW_W_PX,
      h: GLOW_H_PX,
      opacity: 0,
      scale: 0.88,
    };

    const paintGlow = () => {
      host.style.setProperty('--touch-glow-x', `${current.x}px`);
      host.style.setProperty('--touch-glow-w', `${current.w}px`);
      host.style.setProperty('--touch-glow-h', `${current.h}px`);
      host.style.setProperty('--touch-glow-opacity', String(current.opacity));
      host.style.setProperty('--touch-glow-scale', String(current.scale));
    };

    const stopSpring = () => {
      if (springRaf != null) {
        cancelAnimationFrame(springRaf);
        springRaf = null;
      }
    };

    const springTick = () => {
      springRaf = null;
      const ease = reducedMotion ? 1 : SPRING;
      current.x += (target.x - current.x) * ease;
      current.w += (target.w - current.w) * ease;
      current.h += (target.h - current.h) * ease;
      current.opacity += (target.opacity - current.opacity) * ease;
      current.scale += (target.scale - current.scale) * ease;
      paintGlow();

      const drifting =
        Math.abs(target.x - current.x) > SETTLE_EPSILON ||
        Math.abs(target.w - current.w) > SETTLE_EPSILON ||
        Math.abs(target.h - current.h) > SETTLE_EPSILON ||
        Math.abs(target.opacity - current.opacity) > 0.01 ||
        Math.abs(target.scale - current.scale) > 0.01;

      if (drifting) {
        springRaf = requestAnimationFrame(springTick);
      } else {
        current.x = target.x;
        current.w = target.w;
        current.h = target.h;
        current.opacity = target.opacity;
        current.scale = target.scale;
        paintGlow();
      }
    };

    const kickSpring = () => {
      if (springRaf == null) {
        springRaf = requestAnimationFrame(springTick);
      }
    };

    const clearTouchClass = () => {
      host
        .querySelectorAll('.shell-mobile-tab-bar__tab--touch')
        .forEach((el) => {
          el.classList.remove('shell-mobile-tab-bar__tab--touch');
        });
      host.classList.remove('shell-mobile-tab-bar-host--qr-touch');
    };

    /** Clear scrub chrome immediately — no delayed second flash after nav. */
    const endVisualScrub = () => {
      stopSpring();
      current.opacity = 0;
      current.scale = 0.88;
      target.opacity = 0;
      target.scale = 0.88;
      paintGlow();
      clearTouchClass();
      activeButton = null;
    };

    const buttonFromPoint = (x: number, y: number): HTMLElement | null => {
      const stack = document.elementsFromPoint(x, y);
      for (const el of stack) {
        if (!(el instanceof Element)) {
          continue;
        }
        const button = el.closest('ion-tab-button');
        if (button instanceof HTMLElement && host.contains(button)) {
          return button;
        }
      }

      // QR mark sits outside the scaled layer — still map to the QR tab.
      const qrMark = host.querySelector('.shell-mobile-tab-bar__qr-mark');
      if (qrMark instanceof HTMLElement) {
        const qrRect = qrMark.getBoundingClientRect();
        if (
          x >= qrRect.left &&
          x <= qrRect.right &&
          y >= qrRect.top &&
          y <= qrRect.bottom
        ) {
          const qrTab = host.querySelector('.shell-mobile-tab-bar__qr-tab');
          if (qrTab instanceof HTMLElement) {
            return qrTab;
          }
        }
      }

      const buttons = host.querySelectorAll('ion-tab-button');
      let nearest: HTMLElement | null = null;
      let nearestDist = Number.POSITIVE_INFINITY;
      for (const node of buttons) {
        if (!(node instanceof HTMLElement)) {
          continue;
        }
        const rect = node.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const dist = Math.abs(x - cx);
        if (dist < nearestDist && y >= rect.top - 28 && y <= rect.bottom + 28) {
          nearestDist = dist;
          nearest = node;
        }
      }
      return nearest;
    };

    const setGlowTargetForButton = (button: HTMLElement) => {
      if (isQrTab(button)) {
        target.opacity = 0;
        target.scale = 0.88;
        kickSpring();
        return;
      }

      const anchor = glowAnchorForButton(button);
      const layerRect = scaleLayer.getBoundingClientRect();
      const rect = anchor.getBoundingClientRect();
      const scale = readScaleLayerScale(host);
      const centerX = (rect.left + rect.width / 2 - layerRect.left) / scale;

      target.x = centerX - GLOW_W_PX / 2;
      target.w = GLOW_W_PX;
      target.h = GLOW_H_PX;
      target.opacity = 1;
      target.scale = 1;
      kickSpring();
    };

    const setActiveButton = (button: HTMLElement) => {
      if (activeButton === button) {
        if (!isQrTab(button)) {
          setGlowTargetForButton(button);
        }
        return;
      }

      clearTouchClass();
      button.classList.add('shell-mobile-tab-bar__tab--touch');
      if (isQrTab(button)) {
        host.classList.add('shell-mobile-tab-bar-host--qr-touch');
      }
      if (activeButton != null && scrubbing) {
        void triggerSelectionChangedHaptic();
      }
      activeButton = button;
      setGlowTargetForButton(button);
    };

    const armScrubScale = () => {
      if (scrubScaleArmed || reducedMotion) {
        return;
      }
      scrubScaleArmed = true;
      host.classList.add('shell-mobile-tab-bar-host--expanding');
      host.style.setProperty(
        '--shell-tab-bar-scrub-scale',
        String(SCRUB_HOST_SCALE),
      );
    };

    const settleScrubScale = () => {
      if (settleTimer != null) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }

      host.classList.add('shell-mobile-tab-bar-host--settling');
      host.classList.add('shell-mobile-tab-bar-host--expanding');
      host.dispatchEvent(new Event(SHELL_TAB_BAR_SETTLE_EVENT));

      // Ensure slow duration is applied before changing the scale var.
      const releaseScale = () => {
        host.style.setProperty('--shell-tab-bar-scrub-scale', '1');
      };
      if (scrubScaleArmed && !reducedMotion) {
        requestAnimationFrame(releaseScale);
      } else {
        releaseScale();
      }

      settleTimer = setTimeout(() => {
        settleTimer = null;
        host.classList.remove('shell-mobile-tab-bar-host--settling');
        if (!host.classList.contains('shell-mobile-tab-bar-host--scrubbing')) {
          host.classList.remove('shell-mobile-tab-bar-host--expanding');
        }
      }, SCALE_SETTLE_MS);

      scrubScaleArmed = false;
    };

    const flushPointer = () => {
      pointerRaf = null;
      if (!scrubbing) {
        return;
      }

      const moved = Math.hypot(pendingX - startX, pendingY - startY);
      if (moved >= SCRUB_MOVE_THRESHOLD_PX) {
        armScrubScale();
      }

      const button = buttonFromPoint(pendingX, pendingY);
      if (button) {
        setActiveButton(button);
      }
    };

    const queuePointer = (x: number, y: number) => {
      pendingX = x;
      pendingY = y;
      lastPointerX = x;
      lastPointerY = y;
      if (pointerRaf == null) {
        pointerRaf = requestAnimationFrame(flushPointer);
      }
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) {
        return;
      }
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      scrubbing = true;
      scrubScaleArmed = false;
      startX = touch.clientX;
      startY = touch.clientY;
      host.classList.add('shell-mobile-tab-bar-host--scrubbing');
      host.dispatchEvent(new Event(SHELL_TAB_BAR_EXPAND_EVENT));
      host.style.setProperty('--shell-tab-bar-scrub-scale', '1');
      void triggerSelectionStartHaptic();

      const button = buttonFromPoint(touch.clientX, touch.clientY);
      if (button) {
        if (!isQrTab(button)) {
          const anchor = glowAnchorForButton(button);
          const layerRect = scaleLayer.getBoundingClientRect();
          const rect = anchor.getBoundingClientRect();
          const scale = readScaleLayerScale(host);
          const centerX = (rect.left + rect.width / 2 - layerRect.left) / scale;
          current.x = centerX - GLOW_W_PX / 2;
          current.w = GLOW_W_PX;
          current.h = GLOW_H_PX;
          current.opacity = 0.45;
          current.scale = 0.94;
          paintGlow();
        } else {
          current.opacity = 0;
          paintGlow();
        }
        setActiveButton(button);
      } else {
        queuePointer(touch.clientX, touch.clientY);
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!scrubbing || event.touches.length !== 1) {
        return;
      }
      const touch = event.touches[0];
      if (!touch) {
        return;
      }
      queuePointer(touch.clientX, touch.clientY);
    };

    const endScrub = (event: TouchEvent) => {
      if (!scrubbing) {
        return;
      }
      scrubbing = false;
      host.classList.remove('shell-mobile-tab-bar-host--scrubbing');

      const touch = event.changedTouches[0];
      const x = touch?.clientX ?? lastPointerX;
      const y = touch?.clientY ?? lastPointerY;
      const releaseButton = buttonFromPoint(x, y) ?? activeButton;

      void triggerSelectionEndHaptic();

      // Kill glow/touch chrome before navigation so the destination tab
      // never gets a second highlight flash after the page switch.
      endVisualScrub();

      // Slow return to resting scale — survives tab switches via settle event.
      settleScrubScale();

      if (releaseButton) {
        onActivate(releaseButton);
      }
    };

    host.addEventListener('touchstart', onTouchStart, { passive: true });
    host.addEventListener('touchmove', onTouchMove, { passive: true });
    host.addEventListener('touchend', endScrub, { passive: true });
    host.addEventListener('touchcancel', endScrub, { passive: true });

    host.style.setProperty('--shell-tab-bar-scrub-scale', '1');
    endVisualScrub();
    paintGlow();

    return () => {
      host.removeEventListener('touchstart', onTouchStart);
      host.removeEventListener('touchmove', onTouchMove);
      host.removeEventListener('touchend', endScrub);
      host.removeEventListener('touchcancel', endScrub);
      if (pointerRaf != null) {
        cancelAnimationFrame(pointerRaf);
      }
      if (settleTimer != null) {
        clearTimeout(settleTimer);
      }
      stopSpring();
      host.classList.remove('shell-mobile-tab-bar-host--scrubbing');
      // Don't strip settling mid-animation if a tab switch remounts this effect.
      if (!host.classList.contains('shell-mobile-tab-bar-host--settling')) {
        host.classList.remove('shell-mobile-tab-bar-host--expanding');
        host.style.setProperty('--shell-tab-bar-scrub-scale', '1');
      }
      host.classList.remove('shell-mobile-tab-bar-host--qr-touch');
      clearTouchClass();
      glow?.remove();
    };
  }, [enabled, hostRef, onActivate]);
}
